import type {
  MessageGeneration,
  MessageSkillEvent,
  MessageSubagentRun,
  MessageToolCall,
  SkillStreamStatus,
  StreamStatusEntry,
  StreamStatusEntryIcon,
  SubagentStreamStatus,
  ToolCallStreamStatus,
} from '../types/chat';
import { formatSkillDisplayName } from './parse-message-skill-events';
import { getSubagentStreamStatusLabel } from './parse-message-subagent-runs';
import {
  completeInProgressTodos,
  getToolCallStreamStatusLabel,
  isWriteTodosTool,
  parseStreamTodos,
} from './parse-message-tool-calls';

let streamStatusEntryCounter = 0;

function nextStreamStatusEntryId(kind: StreamStatusEntry['kind']): string {
  streamStatusEntryCounter += 1;
  return `${kind}-${Date.now()}-${streamStatusEntryCounter}`;
}

export function buildSkillStreamStatusEntryLabel(
  status: SkillStreamStatus,
  skillName?: string,
  resourceName?: string
): { label: string; icon: StreamStatusEntryIcon } {
  const displayName = skillName?.trim() ? formatSkillDisplayName(skillName) : '';

  switch (status) {
    case 'used':
      return {
        label: displayName ? `Reading ${displayName} skill` : 'Reading skill',
        icon: 'skill_used',
      };
    case 'resource_loaded':
      if (resourceName && displayName) {
        return {
          label: `Reading ${resourceName} skill for context on ${displayName}!`,
          icon: 'skill_resource',
        };
      }
      if (resourceName) {
        return {
          label: `Reading ${resourceName} skill`,
          icon: 'skill_resource',
        };
      }
      return {
        label: displayName ? `Reading ${displayName} skill` : 'Reading skill resource',
        icon: 'skill_resource',
      };
    case 'skipped':
      return {
        label: displayName ? `${displayName} skipped` : 'Skill skipped',
        icon: 'skill_skipped',
      };
    default:
      return { label: 'Skill activity', icon: 'skill_used' };
  }
}

export function buildToolStreamStatusEntryLabel(
  status: ToolCallStreamStatus,
  toolName?: string
): { label: string; icon: StreamStatusEntryIcon } {
  return {
    label: getToolCallStreamStatusLabel(status, toolName),
    icon: status === 'started' ? 'tool_started' : 'tool_completed',
  };
}

export function createSkillStreamStatusEntry(
  skillEntry: MessageSkillEvent,
  status: SkillStreamStatus,
  contentOffset?: number
): StreamStatusEntry {
  const { label, icon } = buildSkillStreamStatusEntryLabel(
    status,
    skillEntry.skillName,
    skillEntry.resourceName
  );
  return {
    id: nextStreamStatusEntryId('skill'),
    kind: 'skill',
    label,
    icon,
    ...(typeof contentOffset === 'number' ? { contentOffset } : {}),
  };
}

export function buildSubagentStreamStatusEntryLabel(
  status: SubagentStreamStatus,
  subagentName?: string
): { label: string; icon: StreamStatusEntryIcon } {
  return {
    label: getSubagentStreamStatusLabel(status, subagentName),
    icon: status === 'started' ? 'subagent_started' : 'subagent_completed',
  };
}

export function createSubagentStreamStatusEntry(
  subagentRun: MessageSubagentRun,
  status: SubagentStreamStatus,
  contentOffset?: number
): StreamStatusEntry {
  const { label, icon } = buildSubagentStreamStatusEntryLabel(status, subagentRun.subagentName);
  return {
    id: nextStreamStatusEntryId('subagent'),
    kind: 'subagent',
    label,
    icon,
    ...(typeof contentOffset === 'number' ? { contentOffset } : {}),
  };
}

export function createToolStreamStatusEntry(
  toolEntry: MessageToolCall,
  status: ToolCallStreamStatus,
  contentOffset?: number
): StreamStatusEntry {
  const { label, icon } = buildToolStreamStatusEntryLabel(status, toolEntry.toolName);
  const name = toolEntry.toolName?.trim();
  const isStarted = status === 'started';
  let labelKey = isStarted ? 'TOOL_CALL_STREAM_STARTED' : 'TOOL_CALL_STREAM_FINISHED';
  if (name) labelKey = isStarted ? 'TOOL_CALL_STARTED_FOR' : 'TOOL_CALL_FINISHED_FOR';
  return {
    id: nextStreamStatusEntryId('tool'),
    kind: 'tool',
    label,
    labelKey,
    ...(name ? { labelValues: { name } } : {}),
    icon,
    ...(typeof contentOffset === 'number' ? { contentOffset } : {}),
  };
}

export type StreamContentBlock =
  | { type: 'text'; text: string }
  | { type: 'status'; entries: StreamStatusEntry[]; offset: number };

/**
 * Where to pin a status in the answer.
 * - Same wave while content length hasn't grown (one Steps for a tool burst).
 * - Completed rows can pass `pairWithIcon` to stay with their started row.
 */
export function nextStatusOffset(
  entries: StreamStatusEntry[] | undefined,
  contentLength: number,
  pairWithIcon?: StreamStatusEntryIcon
): number {
  if (pairWithIcon) {
    for (let i = (entries?.length ?? 0) - 1; i >= 0; i -= 1) {
      const entry = entries![i];
      if (entry.icon === pairWithIcon && typeof entry.contentOffset === 'number') {
        return entry.contentOffset;
      }
    }
  }

  for (let i = (entries?.length ?? 0) - 1; i >= 0; i -= 1) {
    const offset = entries![i].contentOffset;
    if (typeof offset === 'number') {
      return contentLength <= offset ? offset : contentLength;
    }
  }
  return contentLength;
}

/** Split answer text + Steps so each status wave sits where it started. */
export function buildInterleavedStreamBlocks(
  content: string | undefined,
  entries: StreamStatusEntry[] | undefined
): StreamContentBlock[] {
  const text = content ?? '';
  const placed = (entries ?? []).filter(
    (entry) => entry.icon !== 'processing_started' && typeof entry.contentOffset === 'number'
  );

  if (!placed.length) {
    return text ? [{ type: 'text', text }] : [];
  }

  const groups = new Map<number, StreamStatusEntry[]>();
  for (const entry of placed) {
    const offset = Math.min(Math.max(0, entry.contentOffset as number), text.length);
    const list = groups.get(offset) ?? [];
    list.push(entry);
    groups.set(offset, list);
  }

  const blocks: StreamContentBlock[] = [];
  let cursor = 0;

  for (const offset of [...groups.keys()].sort((a, b) => a - b)) {
    const slice = text.slice(cursor, offset);
    if (slice.trim()) {
      blocks.push({ type: 'text', text: slice });
    }

    const entriesAtOffset = groups.get(offset) ?? [];
    const prev = blocks[blocks.length - 1];
    // No real text between waves → keep one Steps block.
    if (prev?.type === 'status') {
      prev.entries.push(...entriesAtOffset);
    } else {
      blocks.push({ type: 'status', entries: entriesAtOffset, offset });
    }
    cursor = offset;
  }

  if (cursor < text.length) {
    blocks.push({ type: 'text', text: text.slice(cursor) });
  }

  return blocks;
}

export function hasInlineStreamSteps(entries: StreamStatusEntry[] | undefined): boolean {
  return (entries ?? []).some((entry) => typeof entry.contentOffset === 'number');
}

export function createProcessingStartedStreamStatusEntry(
  message?: string
): StreamStatusEntry {
  return {
    id: nextStreamStatusEntryId('system'),
    kind: 'system',
    label: message?.trim() || 'Processing your request',
    icon: 'processing_started',
  };
}

export function appendStreamStatusEntry(
  entries: StreamStatusEntry[] | undefined,
  entry: StreamStatusEntry
): StreamStatusEntry[] {
  return [...(entries ?? []), entry];
}

/** Status timeline rows first, todo list rows last. */
export function sortStreamStatusEntriesForDisplay(
  entries: StreamStatusEntry[],
): StreamStatusEntry[] {
  const statusRows: StreamStatusEntry[] = [];
  const todoRows: StreamStatusEntry[] = [];

  for (const entry of entries) {
    if (entry.todos?.length) {
      todoRows.push(entry);
    } else {
      statusRows.push(entry);
    }
  }

  return [...statusRows, ...todoRows];
}

const PERSISTED_TODOS_ENTRY_ID = 'stream-todos-persisted';

/** Latest todo snapshot from any write_todos event in this generation. */
export function getLatestStreamTodos(
  entries?: StreamStatusEntry[],
): StreamStatusEntry['todos'] {
  for (let i = (entries?.length ?? 0) - 1; i >= 0; i -= 1) {
    const todos = entries![i].todos;
    if (todos?.length) {
      return todos;
    }
  }
  return undefined;
}

/** True when any todo is still pending or in progress. */
export function hasUnfinishedStreamTodos(
  todos?: StreamStatusEntry['todos'] | null,
): boolean {
  return (todos ?? []).some((todo) => todo.status !== 'completed');
}

/**
 * Traverses status entries and marks any in_progress todos as completed (strikethrough).
 */
export function completeInProgressStatusEntries(
  entries?: StreamStatusEntry[]
): StreamStatusEntry[] | undefined {
  if (!entries?.length) {
    return entries;
  }
  return entries.map((entry) => {
    if (!entry.todos?.length) {
      return entry;
    }
    const hasInProgress = entry.todos.some((t) => t.status === 'in_progress');
    if (!hasInProgress) {
      return entry;
    }
    return {
      ...entry,
      todos: completeInProgressTodos(entry.todos),
    };
  });
}

/** Keep status rows for this wave; attach this wave's todo snapshot at the bottom. */
export function applyPersistedTodosToStepEntries(
  blockEntries: StreamStatusEntry[],
  waveTodos?: StreamStatusEntry['todos'],
): StreamStatusEntry[] {
  if (!waveTodos?.length) {
    return blockEntries.filter((entry) => !entry.todos?.length);
  }

  const statusRows = blockEntries.filter((entry) => !entry.todos?.length);
  return [
    ...statusRows,
    {
      id: PERSISTED_TODOS_ENTRY_ID,
      kind: 'tool',
      label: '',
      // Completed icon so a todos-only wave never looks like an in-progress tool.
      icon: 'tool_completed',
      todos: waveTodos,
    },
  ];
}

/** Todos snapshot already pinned to this Steps wave (same contentOffset). */
function findTodosEntryIndexAtOffset(
  entries: StreamStatusEntry[],
  contentOffset: number,
): number {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    if (
      (entries[i].todos?.length ?? 0) > 0 &&
      entries[i].contentOffset === contentOffset
    ) {
      return i;
    }
  }
  return -1;
}

function createTodosStreamStatusEntry(
  todos: NonNullable<ReturnType<typeof parseStreamTodos>>,
  contentOffset: number,
): StreamStatusEntry {
  return {
    id: nextStreamStatusEntryId('tool'),
    kind: 'tool',
    label: '',
    icon: 'tool_completed',
    contentOffset,
    todos,
  };
}

/** Append tool status row + optional todos list for write_todos / Update Todos. */
export function upsertTodosStreamStatusEntry(
  entries: StreamStatusEntry[] | undefined,
  toolEntry: MessageToolCall,
  status: ToolCallStreamStatus,
  contentOffset: number,
): StreamStatusEntry[] {
  const todos = parseStreamTodos(toolEntry.arguments);
  const isTodosTool = isWriteTodosTool(toolEntry) || !!todos?.length;
  let list = [...(entries ?? [])];
  const statusEntry = createToolStreamStatusEntry(toolEntry, status, contentOffset);

  if (!isTodosTool) {
    return appendStreamStatusEntry(list, statusEntry);
  }

  // Always append started/finished for each write_todos invocation — earlier logic
  // only kept the first "started" row by tool name, so later Update Todos calls
  // (e.g. 3× started+finished = 6 events) only showed 4 status lines.
  list = appendStreamStatusEntry(list, statusEntry);
  if (todos?.length) {
    // Same Steps wave → update that wave's snapshot. New offset → freeze prior
    // waves and append a new snapshot so earlier Steps keep their todos.
    const sameWaveIdx = findTodosEntryIndexAtOffset(list, contentOffset);
    if (sameWaveIdx >= 0) {
      list[sameWaveIdx] = { ...list[sameWaveIdx], todos };
    } else {
      list = appendStreamStatusEntry(list, createTodosStreamStatusEntry(todos, contentOffset));
    }
  }
  return list;
}

export function hasStreamStatusEntries(
  generation?: Pick<MessageGeneration, 'streamStatusEntries'>
): boolean {
  return (generation?.streamStatusEntries?.length ?? 0) > 0;
}

export function clearStreamStatusEntryFields<T extends MessageGeneration>(generation: T): T {
  const { streamStatusEntries: _entries, ...rest } = generation;
  return rest as T;
}

/** Resolves timeline rows for the post-response dropdown (live entries or metadata fallback). */
export function resolveStreamActivityEntries(
  generation: Pick<
    MessageGeneration,
    'streamStatusEntries' | 'toolCalls' | 'skillEvents' | 'subagentRuns'
  >
): StreamStatusEntry[] {
  if (generation.streamStatusEntries?.length) {
    const completedEntries =
      completeInProgressStatusEntries(generation.streamStatusEntries) ??
      generation.streamStatusEntries;
    return applyPersistedTodosToStepEntries(
      completedEntries,
      getLatestStreamTodos(completedEntries),
    );
  }

  const entries: StreamStatusEntry[] = [];

  for (const skillEvent of generation.skillEvents ?? []) {
    entries.push(createSkillStreamStatusEntry(skillEvent, skillEvent.type));
  }

  for (const subagentRun of generation.subagentRuns ?? []) {
    entries.push(
      createSubagentStreamStatusEntry(subagentRun, 'started'),
      createSubagentStreamStatusEntry(subagentRun, 'completed'),
    );
  }

  for (const toolCall of generation.toolCalls ?? []) {
    entries.push(
      createToolStreamStatusEntry(toolCall, 'started'),
      createToolStreamStatusEntry(toolCall, 'completed'),
    );
  }

  return entries;
}

/** Returns the assistant generation used for live stream UI (no cross-generation merge). */
export function mergeStreamStatusEntriesFromGenerations(
  generations: MessageGeneration[],
  preferredGenerationIndex: number
): MessageGeneration {
  return (
    generations[preferredGenerationIndex] ??
    generations[generations.length - 1] ??
    ({} as MessageGeneration)
  );
}